"use client";
import React, { ChangeEvent, useCallback, useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client/react";

import { TextInput, ThemedPage } from "@/components";
import { Main, Module } from "@/components/layouts";
import PageHeading from "@/components/page-heading";
import { SEARCH } from "@/gql/queries/search";

import { ResultsAnimations } from "./results-animations";
import { ResultsUsers } from "./results-users";
import { useSearchPageParams } from "./use-search-params";

const RESULTS_PER_CATEGORY = 20;
const SEARCH_DEBOUNCE_TIME_MS = 500;

const SearchPage = () => {
  const debounceTimerRef = useRef<NodeJS.Timeout>(undefined);

  const [doSearch, { data }] = useLazyQuery(SEARCH);
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const { searchTerm, animationPage, userPage, updateUrl } =
    useSearchPageParams();

  useEffect(() => {
    if (!searchBoxRef.current) return;
    searchBoxRef.current.focus();

    if (searchTerm) {
      searchBoxRef.current.value = searchTerm;
    }
  }, [searchTerm]);

  const performSearch = useCallback(
    (searchTerm: string) => {
      doSearch({
        variables: {
          animationOffset: (animationPage - 1) * RESULTS_PER_CATEGORY,
          userOffset: (userPage - 1) * RESULTS_PER_CATEGORY,
          limit: RESULTS_PER_CATEGORY,
          searchTerm,
        },
      });

      updateUrl({ searchTerm });
    },
    [animationPage, doSearch, updateUrl, userPage],
  );

  const onSearchTermChanged = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < 3) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      updateUrl({ animationPage: "1", userPage: "1" });
      performSearch(e.target.value);
    }, SEARCH_DEBOUNCE_TIME_MS);
  };

  useEffect(() => {
    if (searchTerm) {
      performSearch(searchTerm);
    }
  }, [searchTerm, animationPage, userPage, performSearch]);

  const onAnimationPageChanged = (newPage: string) => {
    updateUrl({ animationPage: newPage });
  };

  return (
    <ThemedPage>
      <Main>
        <PageHeading>{`Search`}</PageHeading>
        <Module>
          <TextInput
            ref={searchBoxRef}
            className="text-lg"
            onChange={onSearchTermChanged}
            placeholder="Search for studios and animations"
          />
        </Module>

        <Module>
          <ResultsUsers users={data?.search?.users ?? []} />
        </Module>
        <Module>
          <ResultsAnimations
            animations={data?.search?.animations}
            total={data?.search?.animationsCount ?? 0}
            onPageChanged={onAnimationPageChanged}
            page={animationPage}
          />
        </Module>
      </Main>
    </ThemedPage>
  );
};

export default SearchPage;
